/* eslint-disable no-use-before-define */

const CLASSIFICATION_PREUVE = 'preuve';
const CLASSIFICATION_CONTRADICTION = 'contradiction';

export const proposerIdentifiantFlux = (nom) => {
  const slug = String(nom || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'nouveau-flux';
};

export const extraireEvenementsDepuisLog = (log) => (
  String(log || '')
    .split(/\r?\n/)
    .map((ligne, index) => extraireEvenementDepuisLigne(ligne, index + 1))
    .filter(Boolean)
);

export const regrouperEvenements = (evenements) => {
  const groupesParType = {};
  (evenements || []).forEach((evenement) => {
    if (!groupesParType[evenement.type]) {
      groupesParType[evenement.type] = {
        type: evenement.type,
        occurrences: [],
        ligneRepresentative: evenement.ligne,
        suggestion: suggererRole(evenement.type),
      };
    }
    groupesParType[evenement.type].occurrences.push(evenement);
  });

  return Object.keys(groupesParType)
    .sort((a, b) => a.localeCompare(b, 'fr'))
    .map((type) => ({
      ...groupesParType[type],
      nombreOccurrences: groupesParType[type].occurrences.length,
    }));
};

export const genererFluxBrouillon = (formulaire) => {
  const maintenant = new Date().toISOString().slice(0, 10);
  const idCourt = proposerIdentifiantFlux(formulaire && formulaire.identifiant);
  const fluxId = `flux:${idCourt}`;
  const nom = (formulaire && formulaire.nom) || idCourt;

  return {
    type_objet: 'flux',
    version_schema: 'flux.schema.v1',
    identite: {
      id: fluxId,
      nom,
      domaine: 'synccam',
      statut: 'brouillon',
      version_flux: 'v0.1',
      alias: [idCourt],
    },
    mission: {
      declencheur_metier: (formulaire && formulaire.mission) || '',
      resultat_attendu: (formulaire && formulaire.descriptionCourte) || '',
      conditions_succes_metier: [],
      conditions_echec_metier: [],
    },
    perimetre: {
      inclus: ['source:synccam_log'],
      exclus: [],
      granularite: (formulaire && formulaire.granularite) || 'segment',
    },
    objets_traverses: [],
    interfaces: {
      amont: [
        {
          id: 'source:synccam_log',
          type_interface: 'source_runtime',
          contrat: ['contrat:log_structure_event'],
        },
      ],
      aval: [],
    },
    jalons: [],
    dependances: {
      flux_amont: [],
      flux_aval: [],
      objets_externes: ['source:synccam_log'],
    },
    criticite: {
      niveau: 'gris',
      impact_client: 'a_qualifier',
      impact_operationnel: 'a_qualifier',
    },
    exposition_cockpit: {
      visible: false,
      ordre: null,
      groupe: null,
      libelle_court: nom,
      actions_rapides: [],
    },
    versionnage: {
      cree_le: maintenant,
      revise_le: maintenant,
      proprietaire: 'agent:monitoring',
    },
  };
};

export const genererProfilBrouillon = (formulaire, annotations) => {
  const idCourt = proposerIdentifiantFlux(formulaire && formulaire.identifiant);
  const fluxId = `flux:${idCourt}`;
  const preuvesAttendues = annotationsUtiles(annotations, CLASSIFICATION_PREUVE)
    .map((annotation, index) => construirePreuve(annotation, index + 1, false));
  const preuvesContradictoires = annotationsUtiles(annotations, CLASSIFICATION_CONTRADICTION)
    .map((annotation, index) => construirePreuve(annotation, index + 1, true));
  const preuvesRequises = preuvesAttendues
    .filter((preuve) => preuve.force_probante === 'requise')
    .map((preuve) => preuve.id);
  const idsContradictions = preuvesContradictoires.map((preuve) => preuve.id);

  const reglesInterpretation = [
    idsContradictions.length > 0 && {
      id: 'regle:brouillon_incident',
      priorite: 10,
      si: {
        toutes: [],
        une_des: idsContradictions,
        aucune: [],
      },
      alors: {
        etat: 'etat:incident',
        confiance: 'forte',
        actions: [],
      },
    },
    {
      id: 'regle:brouillon_nominal',
      priorite: 20,
      si: {
        toutes: preuvesRequises,
        une_des: [],
        aucune: idsContradictions,
      },
      alors: {
        etat: 'etat:nominal',
        confiance: 'moyenne',
        actions: [],
      },
    },
    preuvesAttendues.length > 0 && {
      id: 'regle:brouillon_preuve_insuffisante',
      priorite: 30,
      si: {
        toutes: [],
        une_des: preuvesAttendues.map((preuve) => preuve.id),
        aucune: idsContradictions,
      },
      alors: {
        etat: 'etat:preuve_insuffisante',
        confiance: 'faible',
        actions: [],
      },
    },
  ].filter(Boolean);

  return {
    type_objet: 'profil_interpretation_flux',
    version_schema: 'profil-interpretation-flux.schema.v1',
    identite: {
      id: `profil-interpretation:${fluxId}:v1`,
      flux_id: fluxId,
      nom: `Profil d'interpretation ${formulaire && formulaire.nom ? formulaire.nom : idCourt}`,
      statut: 'brouillon',
      version_profil: 'v0.1',
    },
    portee: {
      jalons_couverts: preuvesAttendues.map((preuve) => preuve.jalon).filter(Boolean),
      objets_couverts: ['source:synccam_log'],
      sources_runtime: ['source:synccam_log'],
    },
    observation: {
      frequence_evaluation_sec: 30,
      cles_correlation: [],
      retention_preuves_jours: 30,
      sla_detection_sec: 120,
    },
    preuves_attendues: preuvesAttendues,
    preuves_contradictoires: preuvesContradictoires,
    contradictions: idsContradictions.map((id) => ({
      id: `contradiction:${id.replace(/^preuve:/, '')}`,
      preuves_incompatibles: [id],
      raison: 'Preuve contradictoire detectee dans le log brouillon.',
      etat_induit: 'etat:incident',
      confiance: 'forte',
    })),
    etats: [
      {
        id: 'etat:nominal',
        categorie: 'nominal',
        severite: 'info',
        terminal: true,
        visible_cockpit: false,
        declencheurs: ['regle:brouillon_nominal'],
      },
      {
        id: 'etat:preuve_insuffisante',
        categorie: 'surveillance',
        severite: 'jaune',
        terminal: false,
        visible_cockpit: false,
        declencheurs: ['regle:brouillon_preuve_insuffisante'],
      },
      {
        id: 'etat:incident',
        categorie: 'incident',
        severite: 'rouge',
        terminal: true,
        visible_cockpit: false,
        declencheurs: idsContradictions.concat(['regle:brouillon_incident']),
      },
    ],
    regles_interpretation: reglesInterpretation,
    transitions: [],
    versionnage: {
      cree_le: new Date().toISOString().slice(0, 10),
      revise_le: new Date().toISOString().slice(0, 10),
      proprietaire: 'agent:monitoring',
    },
  };
};

export const genererEntreeIndexBrouillon = (formulaire) => {
  const idCourt = proposerIdentifiantFlux(formulaire && formulaire.identifiant);
  return {
    fichiers_a_ajouter: [
      `flux-${idCourt}.json`,
      `profil-interpretation-${idCourt}.json`,
    ],
  };
};

export const fusionnerRegistreAvecBrouillon = (registre, flux, profil) => {
  const registreSource = registre || { flux: [], profils: [], profilsParFlux: {}, objets: [] };
  const fluxId = flux && flux.identite && flux.identite.id;
  const profilsParFlux = { ...(registreSource.profilsParFlux || {}) };
  if (fluxId && profil) profilsParFlux[fluxId] = profil;

  return {
    ...registreSource,
    flux: remplacerParId(registreSource.flux || [], flux),
    profils: remplacerParId(registreSource.profils || [], profil),
    profilsParFlux,
    objets: remplacerParId(registreSource.objets || [], flux).concat(profil ? [profil] : []),
    registreTemporaire: true,
    genereA: new Date().toISOString(),
  };
};

const extraireEvenementDepuisLigne = (ligne, numeroLigne) => {
  const texte = String(ligne || '').trim();
  if (!texte) return null;
  const champs = extraireChampsStructures(texte);
  const type = champs.event;
  if (!type) return null;

  return {
    type,
    ligne: texte,
    numeroLigne,
    timestamp: extraireTimestamp(texte),
    champs,
  };
};

const extraireChampsStructures = (ligne) => {
  const champs = {};
  ligne.split('|').forEach((fragment) => {
    const propre = fragment.trim();
    const separation = propre.indexOf('=');
    if (separation <= 0) return;
    const cle = propre.slice(0, separation).trim();
    const valeur = propre.slice(separation + 1).trim();
    if (cle) champs[cle] = valeur;
  });
  return champs;
};

const extraireTimestamp = (ligne) => {
  const premierFragment = String(ligne || '').split('|')[0].trim();
  return premierFragment.indexOf('=') >= 0 ? '' : premierFragment;
};

const annotationsUtiles = (annotations, classification) => (
  (annotations || [])
    .filter((annotation) => annotation && annotation.classification === classification)
);

const construirePreuve = (annotation, index, contradictoire) => {
  const nomMetier = annotation.nomMetier || annotation.type;
  const idBase = proposerIdentifiantFlux(nomMetier || annotation.type || `preuve-${index}`);
  const id = `preuve:${idBase}`;
  const preuve = {
    id,
    type_preuve: 'evenement_runtime',
    force_probante: annotation.forceProbante || 'contextuelle',
    source: {
      type_source: 'log',
      id_source: 'source:synccam_log',
    },
    selecteur: {
      champ: 'message',
      operateur: 'contient',
      valeur: `event=${annotation.type}`,
    },
    fenetre_sec: 900,
  };

  if (contradictoire) {
    return {
      ...preuve,
      etat_induit: 'etat:incident',
    };
  }

  return {
    ...preuve,
    jalon: `jalon:${idBase}`,
    nom_metier: nomMetier,
  };
};

const suggererRole = (type) => {
  const valeur = String(type || '').toLowerCase();
  if (/(error|fail|failed|ko|warn|timeout|exception|abort|denied)/.test(valeur)) return CLASSIFICATION_CONTRADICTION;
  if (/(ok|success|done|complete|finalize|created|saved)/.test(valeur)) return CLASSIFICATION_PREUVE;
  return 'indetermine';
};

const remplacerParId = (items, item) => {
  if (!item || !item.identite || !item.identite.id) return (items || []).slice();
  const id = item.identite.id;
  const sansItem = (items || []).filter((existant) => !existant || !existant.identite || existant.identite.id !== id);
  return sansItem.concat(item);
};



// WEBPACK FOOTER //
// src/components/cockpit/atelierCreationFluxService.js